import { ArrowBackRounded } from "@mui/icons-material";
import { useSuspenseQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { ComparePlansButton } from "components/ComparePlansModal";
import { SubscriptionPlanBenefits } from "components/ComparePlansModal/SubscriptionPlan";
import Button from "components/ui/Button";
import Text from "components/ui/Text";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { paymentMethodsQuery } from "services/paymentMethods";
import { availablePricingModelsQuery } from "services/pricingModel";
import { useTracking } from "services/productAnalytics";
import {
  currentSubscriptionPlanQuery,
  executeCardEntryPurchase,
  executeExpressCheckoutPurchase,
  executeSavedMethodPurchase,
  usePricingItemPurchaseMutation,
} from "services/subscriptions";
import {
  PricingModel,
  PricingModelItem,
  PurchaseStudentType,
} from "../../services/pricingModel/model";
import ExistingPaymentMethods from "./ExistingPaymentMethod";
import PaymentModeSelector, { PurchaseMode } from "./PaymentModeSelector";
import PlanSelector from "./PlanSelector";
import styles from "./PurchasePlan.module.scss";

// don't load Stripe prematurely
const LazyAddNewPaymentMethod = lazy(() => import("./AddNewPaymentMethod"));

type PurchasePlanProps = {
  type?: PurchaseStudentType;
  backButtonLabel?: string;
  onBack?: () => void;
  onSuccess: (purchasedItem: PricingModelItem) => void;
  className?: string;
};

function PurchasePlan(props: PurchasePlanProps) {
  const { t } = useTranslation();

  const { data: pricingModels } = useSuspenseQuery(
    availablePricingModelsQuery({ type: props.type })
  );
  const { data: paymentMethods } = useSuspenseQuery(paymentMethodsQuery());
  const { data: currentPlanData } = useSuspenseQuery(currentSubscriptionPlanQuery());

  // Both read the first of a list that can be empty, so both say so: an empty
  // catalogue is the one case this screen has nothing to render.
  const [selectedPricingModel, setSelectedPricingModel] = useState<
    PricingModel | undefined
  >(() => pricingModels.find(model => model.is_selected) ?? pricingModels[0]);
  const [selectedItem, setSelectedItem] = useState<PricingModelItem | undefined>(
    selectedPricingModel?.items[0]
  );

  useTracking(
    {
      name: "purchase_form_shown",
      type: props.type ?? "all",
      pricingModelCode: selectedPricingModel?.code ?? "",
    },
    []
  );

  const cardEntryPurchase = usePricingItemPurchaseMutation(
    selectedItem,
    executeCardEntryPurchase
  );
  const expressCheckoutPurchase = usePricingItemPurchaseMutation(
    selectedItem,
    executeExpressCheckoutPurchase
  );
  const existingPaymentMethodPurchase = usePricingItemPurchaseMutation(
    selectedItem,
    executeSavedMethodPurchase
  );
  const [expressCheckoutSubmitting, setExpressCheckoutSubmitting] = useState(false);

  const purchasing =
    existingPaymentMethodPurchase.isPending ||
    cardEntryPurchase.isPending ||
    expressCheckoutPurchase.isPending ||
    expressCheckoutSubmitting;

  useEffect(() => {
    setSelectedItem(selectedPricingModel?.items[0]);
  }, [selectedPricingModel]);

  const [mode, setMode] = useState<PurchaseMode>(
    paymentMethods.length > 0 ? "on-file" : "new"
  );

  // No pricing model, or one with no items, means there is nothing purchasable.
  // Every hook above has to run first, so the check cannot come any earlier.
  if (!selectedPricingModel || !selectedItem) {
    return null;
  }

  // Guard against double-billing: an active subscriber must not be able to
  // re-purchase the exact plan they are already on. Selecting a *different*
  // item remains a legitimate plan change.
  const activePlan = currentPlanData.subscription;
  const isCurrentPlan =
    activePlan?.status === "active" &&
    activePlan.type === "regular" &&
    activePlan.pricing_model_item !== null &&
    activePlan.pricing_model_item.id === selectedItem.id;

  return (
    <div className={classnames("plan-detail-upgrade-row", props.className)}>
      <PurchasePlanHeader
        backButtonLabel={props.backButtonLabel}
        onBack={props.onBack}
        selectedPricingModel={selectedPricingModel}
      />

      <div className={styles.paymentContainer}>
        <div className={styles.planColumn}>
          <div className={styles.selectedPlan}>
            <div className={styles.selectedPlanInfo}>
              <Text variant="heading-2" align="left" color="var(--action-ink)">
                {selectedPricingModel.name}
              </Text>
            </div>

            {pricingModels.length > 1 && !purchasing && (
              <ComparePlansButton
                purchaseType={props.type}
                selectedPricingModel={selectedPricingModel}
                onPricingModelSelected={setSelectedPricingModel}
                variant="secondary"
              >
                {t("Common.purchasePlan.changePlan")}
              </ComparePlansButton>
            )}
          </div>

          <PlanSelector
            pricingModel={selectedPricingModel}
            selectedItem={selectedItem}
            setSelectedPlan={setSelectedItem}
            disabled={purchasing}
          />

          {selectedPricingModel.benefits && (
            <SubscriptionPlanBenefits benefits={selectedPricingModel.benefits} />
          )}
        </div>

        <div className={styles.paymentColumn}>
          {isCurrentPlan && (
            <Text
              as="p"
              variant="subtitle-3"
              align="center"
              color="var(--action-ink)"
              className={styles.currentPlanNotice}
            >
              {t("Common.purchasePlan.currentPlanNotice")}
            </Text>
          )}

          {!isCurrentPlan && paymentMethods.length > 0 && (
            <PaymentModeSelector
              selectedMode={mode}
              onSelect={setMode}
              purchasing={purchasing}
            />
          )}

          {!isCurrentPlan && mode === "on-file" && (
            <ExistingPaymentMethods
              onSavedMethodPurchase={existingPaymentMethodPurchase.mutateAsync}
              paymentMethods={paymentMethods}
              pricingModelItem={selectedItem}
              onSuccess={() => props.onSuccess(selectedItem)}
              purchasing={purchasing}
            >
              {purchasing && (
                <Text
                  as="p"
                  variant="subtitle-3"
                  align="center"
                  color="var(--action-ink)"
                  className={styles.windowWarning}
                >
                  {t("Common.checkout.doNotCloseWindow")}
                </Text>
              )}
            </ExistingPaymentMethods>
          )}

          {!isCurrentPlan && mode === "new" && (
            <Suspense fallback={null}>
              <LazyAddNewPaymentMethod
                onCardEntryPurchase={cardEntryPurchase.mutateAsync}
                onExpressCheckoutPurchase={expressCheckoutPurchase.mutateAsync}
                onExpressCheckoutBusyChange={setExpressCheckoutSubmitting}
                plan={selectedItem}
                onSuccess={() => props.onSuccess(selectedItem)}
                purchasing={purchasing}
              >
                {purchasing && (
                  <Text
                    as="p"
                    variant="subtitle-3"
                    align="center"
                    color="var(--action-ink)"
                    className={styles.windowWarning}
                  >
                    {t("Common.checkout.doNotCloseWindow")}
                  </Text>
                )}
              </LazyAddNewPaymentMethod>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}

type HeaderProps = {
  backButtonLabel?: string;
  onBack?: () => void;
  selectedPricingModel: PricingModel;
};

function PurchasePlanHeader(props: HeaderProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.header}>
      {props.backButtonLabel !== undefined && (
        <Button
          size="large"
          variant="plain"
          Icon={ArrowBackRounded}
          onClick={props.onBack}
        >
          {props.backButtonLabel}
        </Button>
      )}

      <div>
        <Text as="h3" variant="heading-2" align="center">
          {t(
            props.selectedPricingModel.type === "self-study"
              ? "Common.purchasePlan.activateYourPlan"
              : "Common.purchasePlan.purchaseLessons"
          )}
        </Text>
        <Text as="h4" variant="body-3" align="center">
          {t("Common.purchasePlan.addPaymentDetailsToStartLearning")}
        </Text>
      </div>
    </div>
  );
}

export default PurchasePlan;
